import { Tag } from "@/components/formation";
import MarketResult from "@/components/headToHead/marketResult/MarketResult";
import TeamPlayerAvatar from "@/components/teamPlayerAvatar/TeamPlayerAvatar";
import { PERSON_FALLBACK_IMAGE } from "@/constants/media";
import { formatOdds } from "@/utils/scores";
import type { Maybe, BetSuccessWithStat } from "@/graphql/gql/graphql";
import styles from "./playerPropResult.module.scss";

type PlayerPropResultProps = {
  odds: Maybe<BetSuccessWithStat>;
};

/**
 * This function determines how to display historical odds.
 * Historical odds were originally designed to record only if the over was crossed,
 * not to record odds for over-under. We use the line, odds, and success fields to
 * determine the display format.
 *
 * For anytime touchdown (TD) props, "line" is always null so we use the "odds" value
 * and format it accordingly with "formatOdds" to prefix with "+" or "-".
 * Example:
 * {
 *   "line": null,
 *   "odds": 500,
 *   "success": true,
 * }
 *
 * For over props, "odds" is always null so use the "line" value and prefix it with "O".
 * Example:
 * {
 *   "line": 252.5,
 *   "odds": null,
 *   "success": true,
 * }
 */
export const formatHistoricalOdds = (odds: Maybe<BetSuccessWithStat>) => {
  if (odds?.line) {
    return `O ${odds.line}`;
  } else if (odds?.odds) {
    return formatOdds(odds.odds);
  }

  return "-";
};

export const abbreviatePlayerName = (
  firstName?: Maybe<string>,
  lastName?: Maybe<string>
) => {
  const first = firstName ?? "-";
  const last = lastName ?? "-";
  if (first === "-" && last === "-") {
    return "-";
  }
  return `${first.charAt(0)}. ${last}`;
};

const PlayerPropResult = ({ odds }: PlayerPropResultProps) => {
  const primaryImgName = `${odds?.team?.shortName} Jersey`;
  const primaryImgUrl = odds?.team?.teamJerseySVG ?? PERSON_FALLBACK_IMAGE;
  const position = odds?.player?.position ?? "-";
  const abbreviatedPlayerName = abbreviatePlayerName(
    odds?.player?.firstName,
    odds?.player?.lastName
  );
  const playerName = odds?.player?.name ?? "-";
  const gameStat = odds?.gameStat ?? "-";
  const seasonStat = odds?.seasonStat ?? "-";
  const propOdds = formatHistoricalOdds(odds);
  const hit = odds?.success ?? false;

  return (
    <div className={styles.container}>
      <div className={styles.marketInfo}>
        <TeamPlayerAvatar
          primaryImgName={primaryImgName}
          primaryImgUrl={primaryImgUrl}
          primaryImgSize={40}
          primaryImgClassName={styles.primaryImg}
        />
        <div className={styles.playerInfo}>
          <div className={styles.nameAndPositionContainer}>
            <Tag>{position}</Tag>
            <span className={styles.abbreviatedPlayerName}>
              {abbreviatedPlayerName}
            </span>
            <span className={styles.playerName}>{playerName}</span>
          </div>
          <div className={styles.statContainer}>
            <div className={styles.stat}>
              <div className={styles.label}>Game</div>
              <span className={styles.value}>{gameStat}</span>
            </div>
            <div className={styles.stat}>
              <div className={styles.label}>Season</div>
              <span className={styles.value}>{seasonStat}</span>
            </div>
          </div>
        </div>
      </div>
      <hr className={styles.divider} />
      <div className={styles.marketResult}>
        <MarketResult odds={propOdds} hit={hit} />
      </div>
    </div>
  );
};

export default PlayerPropResult;
